import { useEffect, useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "../../assets/action-items/slideOver-delete.svg";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import EditTemplates from "../MeetingDetailsPage/EditTemplates";
import UserInteractionsService from "../../services/interactions/Interactions";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { TemplateItem } from "../../interfaces/templateBasedExtraction";
import "../../styles/templateBasedExtraction.css";
import CustomConfirmation from "../Common/CustomConfirmation";

const getTemplates = (data: any): TemplateItem[] => {
  const items = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
  return items.map((item: any) => ({
    ...item,
    id: String(item.templateId || item.id || ""),
    name: item.templateName || item.name || "",
    prompt: item.templatePrompt || item.prompt || "",
    scope: item.templateScope || item.scope,
    isOwner: item.isOwner === true,
  })).filter((item: TemplateItem) => Boolean(item.id));
};

const getTemplateIdFromResponse = (response: any): string | undefined => {
  for (const payload of [response, response?.data, response?.data?.data]) {
    const id = payload?.templateId || payload?.id;
    if (id) return String(id);
  }
  return undefined;
};

const TemplateSettings = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useDisplaySnackbar();
  const { data, isLoading, refetch } = UserInteractionsService.useGetTemplateList();
  const { mutateAsync: saveTemplate, isPending } = UserInteractionsService.usePostTemplateCreateOrUpdate();
  const { mutateAsync: deleteTemplate, isPending: isDeleting } = UserInteractionsService.useDeleteTemplate();
  const templates = useMemo(() => getTemplates(data), [data]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const previousSelectedIdRef = useRef<string | null>(null);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const selectedTemplate = templates.find((item) => item.id === selectedId);
  const hasUnsavedChanges = isCreating
    ? Boolean(name.trim() || prompt.trim())
    : Boolean(
        selectedTemplate &&
          (name.trim() !== (selectedTemplate.name || "").trim() ||
            prompt.trim() !== (selectedTemplate.prompt || "").trim()),
      );

  useEffect(() => {
    if (isCreating || isLoading) return;
    if (selectedId && templates.some((item) => item.id === selectedId)) return;
    if (templates.length > 0) {
      setSelectedId(templates[0].id);
      return;
    }
    setIsCreating(true);
    setIsEditing(true);
  }, [isCreating, isLoading, selectedId, templates]);

  useEffect(() => {
    if (isCreating || isEditing) return;
    const selected = templates.find((item) => item.id === selectedId);
    if (!selected) return;
    setName(selected.name || "");
    setPrompt(selected.prompt || "");
    setIsEditing(false);
  }, [isCreating, isEditing, selectedId, templates]);

  const startCreate = () => {
    if (isCreating) return;
    previousSelectedIdRef.current = selectedId;
    setIsCreating(true);
    setIsEditing(true);
    setName("");
    setPrompt("");
  };

  const restoreSelectedTemplate = (id: string | null) => {
    const selected = templates.find((item) => item.id === id);
    setSelectedId(selected?.id || null);
    setName(selected?.name || "");
    setPrompt(selected?.prompt || "");
  };

  const requestTransition = (action: () => void) => {
    if (hasUnsavedChanges) {
      pendingActionRef.current = action;
      setIsDiscardDialogOpen(true);
      return;
    }
    action();
  };

  const cancelCreate = () => {
    const previousId = previousSelectedIdRef.current;
    const fallbackId = previousId || templates[0]?.id || null;
    previousSelectedIdRef.current = null;
    setIsCreating(false);
    setIsEditing(false);
    restoreSelectedTemplate(fallbackId);
  };

  const handleCancel = () => {
    if (isCreating) {
      requestTransition(cancelCreate);
      return;
    }
    requestTransition(() => {
      restoreSelectedTemplate(selectedId);
      setIsEditing(false);
    });
  };

  const handleSave = async (): Promise<boolean> => {
    const trimmedName = name.trim();
    const trimmedPrompt = prompt.trim();
    if (!trimmedName || !trimmedPrompt) {
      showSnackbar("error", !trimmedName ? "Please provide a template name" : "Please provide an extraction prompt");
      return false;
    }
    const selected = templates.find((item) => item.id === selectedId);
    if (!isCreating && selected?.isOwner !== true) {
      showSnackbar("error", "You can only edit templates that you own");
      return false;
    }
    try {
      const previousIds = new Set(templates.map((item) => item.id));
      const response = await saveTemplate({
        ...(isCreating ? {} : { templateId: selected?.templateId || selected?.id }),
        templateName: trimmedName,
        templatePrompt: trimmedPrompt,
      });
      await queryClient.invalidateQueries({ queryKey: ["get_template_list"] });
      const updated = await refetch();
      const savedItems = getTemplates(updated.data);
      const responseId = getTemplateIdFromResponse(response);
      const saved = responseId
        ? savedItems.find((item) => item.id === responseId)
        : isCreating
          ? savedItems.find((item) => !previousIds.has(item.id))
          : savedItems.find((item) => item.id === selected?.id);
      const savedId = saved?.id || responseId || (!isCreating ? selected?.id : undefined);
      if (!savedId) {
        showSnackbar("error", "Template saved, but it could not be selected. Please refresh the template list.");
        return false;
      }
      setIsCreating(false);
      setIsEditing(false);
      setSelectedId(savedId);
      setName(trimmedName);
      setPrompt(trimmedPrompt);
      showSnackbar("success", `Template ${isCreating ? "created" : "updated"} successfully`);
      return true;
    } catch (error: any) {
      showSnackbar("error", error?.response?.data?.message || error?.message || "Failed to save template");
      return false;
    }
  };

  const handleDelete = () => {
    const selected = templates.find((item) => item.id === selectedId);
    if (selected?.isOwner !== true) {
      showSnackbar("error", "You can only delete templates that you own");
      return;
    }
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    const selected = templates.find((item) => item.id === selectedId);
    if (selected?.isOwner !== true) {
      setIsDeleteDialogOpen(false);
      showSnackbar("error", "You can only delete templates that you own");
      return;
    }
    try {
      await deleteTemplate({ templateId: selected.templateId || selected.id });
      await queryClient.invalidateQueries({ queryKey: ["get_template_list"] });
      const updated = await refetch();
      const remainingTemplates = getTemplates(updated.data);
      showSnackbar("success", "Template deleted successfully");
      setName("");
      setPrompt("");
      if (remainingTemplates.length) {
        const nextTemplate = remainingTemplates[0];
        setIsCreating(false);
        setIsEditing(false);
        previousSelectedIdRef.current = null;
        setSelectedId(nextTemplate.id);
        setName(nextTemplate.name || "");
        setPrompt(nextTemplate.prompt || "");
      } else {
        setSelectedId(null);
        setIsCreating(true);
        setIsEditing(true);
      }
      setIsDeleteDialogOpen(false);
    } catch (error: any) {
      showSnackbar("error", error?.response?.data?.message || error?.message || "Failed to delete template");
      setIsDeleteDialogOpen(false);
    }
  };

  const handleDiscardAndContinue = () => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setIsDiscardDialogOpen(false);
    action?.();
  };

  const handleSaveAndContinue = async () => {
    if (await handleSave()) handleDiscardAndContinue();
  };

  const selectTemplate = (item: TemplateItem) => {
    requestTransition(() => {
      previousSelectedIdRef.current = null;
      setIsCreating(false);
      setIsEditing(false);
      setSelectedId(item.id);
      setName(item.name || "");
      setPrompt(item.prompt || "");
    });
  };

  const nameActions = !isCreating && !isEditing && selectedTemplate?.isOwner ? (
    <Box className="template-settings-template-actions">
      <Button
        size="small"
        variant="outlined"
        startIcon={<EditOutlinedIcon />}
        onClick={() => {
          if (selectedTemplate?.isOwner === true) setIsEditing(true);
        }}
      >
        Edit
      </Button>
      <Tooltip title="Delete template">
        <IconButton
          size="small"
          className="template-run-delete-btn"
          onClick={handleDelete}
          disabled={isDeleting}
          aria-label="Delete template"
        >
          {isDeleting ? <CircularProgress size={16} /> : <img src={DeleteIcon} alt="" />}
        </IconButton>
      </Tooltip>
    </Box>
  ) : null;

  return (
    <Box className="template-settings-page">
      <Typography className="template-settings-title">Meeting Extraction Templates</Typography>
      <Typography className="template-settings-subtitle">
        Create and manage custom templates to extract the insights and information that matter most to you from your meetings.
      </Typography>
      <Box className="template-settings-layout">
        <Box className="template-settings-list">
          <Box
            onClick={() => !isCreating && requestTransition(startCreate)}
            className="existing-templates-create-card"
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                if (!isCreating) requestTransition(startCreate);
              }
            }}
          >
            <Box className="existing-templates-create-icon">
              <AddIcon sx={{ color: "#FFF", fontSize: "20px" }} />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography className="existing-templates-create-title">
                Create new template
              </Typography>
              <Typography className="existing-templates-create-subtitle">
                Define your own extraction
              </Typography>
            </Box>
          </Box>
          {isCreating && (
            <Button className="template-settings-list-item selected" onClick={() => !isCreating && requestTransition(startCreate)}>
              New Template
            </Button>
          )}
          {isLoading ? <CircularProgress size={20} sx={{ mt: 2 }} /> : templates.map((item) => (
            <Button
              key={item.id}
              className={`template-settings-list-item ${!isCreating && selectedId === item.id ? "selected" : ""}`}
              onClick={() => selectTemplate(item)}
            >
              {item.name}
            </Button>
          ))}
        </Box>
        {(isCreating || templates.some((item) => item.id === selectedId)) ? <Box className={`template-settings-editor ${isCreating ? "is-creating" : isEditing ? "is-editing" : "is-viewing"}`}>
          <Box className={`template-settings-form ${isCreating ? "is-creating" : isEditing ? "is-editing" : "is-viewing"}`}>
            <EditTemplates
              templateName={name}
              prompt={prompt}
              onTemplateNameChange={setName}
              onPromptChange={setPrompt}
              disabled={isPending || isDeleting}
              readOnly={!isCreating && !isEditing}
              nameActions={nameActions}
              promptRows={8}
              showGuidance={isCreating}
              footer={(isCreating || isEditing) && (
                <Box className="template-settings-actions">
                  <Button variant="outlined" onClick={handleCancel} className="template-edit-cancel-btn">Cancel</Button>
                  <Button variant="contained" onClick={handleSave} disabled={isPending || isDeleting || !hasUnsavedChanges || !name.trim() || !prompt.trim()} className="template-edit-save-btn">
                    {isPending && <CircularProgress size={15} sx={{ mr: 1, color: "inherit" }} />}
                    {isCreating ? "Create template" : "Save changes"}
                  </Button>
                </Box>
              )}
            />
          </Box>
        </Box> : <Box className="template-settings-empty">Select a template to view its details.</Box>}
      </Box>
      <CustomConfirmation
        isOpen={isDiscardDialogOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Do you want to save them before continuing?"
        width="380px"
        actionBtnLabel="Save"
        cancelBtnLabel="Discard"
        handleSuccess={() => void handleSaveAndContinue()}
        handleClose={handleDiscardAndContinue}
        handleCancel={() => setIsDiscardDialogOpen(false)}
        backgroundcolor
        actionType="save"
      />
      <CustomConfirmation
        isOpen={isDeleteDialogOpen}
        title="Delete template?"
        message={`Are you sure you want to delete the “${selectedTemplate?.name || ""}” template? This action cannot be undone.`}
        width="380px"
        actionBtnLabel="Delete"
        cancelBtnLabel="Cancel"
        isLoading={isDeleting}
        handleSuccess={() => void confirmDelete()}
        handleClose={() => setIsDeleteDialogOpen(false)}
        handleCancel={() => setIsDeleteDialogOpen(false)}
        actionType="delete"
      />
    </Box>
  );
};

export default TemplateSettings;
